import { quoteWorkbenchBusinessModeDisplayName } from './businessModes';
import {
  purchaseFinalPriceTax13,
  quoteFinalRentalSubtotalCents,
} from './quoteTotals';
import type {
  CpqState,
  Quote,
  QuoteOutputBusinessSectionSnapshot,
} from './types';

export const OUTPUT_TERM_FIELDS = [
  ['rentalMethod', '租赁方式'],
  ['creditReview', '信用审核'],
  ['depositAmount', '押金金额'],
  ['monthlyRent', '月租金'],
  ['rentalStartTerm', '起租期'],
  ['complimentaryService', '赠送服务'],
  ['freight', '运费'],
  ['quoteValidUntil', '报价有效期'],
] as const;

export type OutputTermKey = (typeof OUTPUT_TERM_FIELDS)[number][0];
export type OutputTermsDisplayOverrides = Partial<
  Record<OutputTermKey, string>
>;

/** 旧报价把通用的“租赁”保存成了展示覆盖值；业务模式名称应优先显示。 */
export function outputTermDisplayValue<T extends string | number | null>(
  key: OutputTermKey,
  fallback: T,
  overrides?: OutputTermsDisplayOverrides,
): string | T {
  if (!Object.hasOwn(overrides ?? {}, key)) return fallback;
  const override = overrides?.[key] ?? '';
  return key === 'rentalMethod' && override === '租赁' ? fallback : override;
}

/** 旧系统 bottomtable 的报价有效期展示默认值；不改变报价版本的真实有效期。 */
export const DEFAULT_OUTPUT_QUOTE_VALIDITY_TEXT =
  '报价7天内有效，过期需联系经理重新报价';

/** Build the local-demo business terms that customer links and CSV exports freeze. */
export function quoteOutputBusinessSections(
  quote: Quote,
  state: Pick<CpqState, 'catalog'>,
): QuoteOutputBusinessSectionSnapshot[] {
  if (quote.outputTermsRequired === false) return [];

  if (quote.businessModeCode === 'buyout') {
    const amounts = quote.items.map((item) =>
      purchaseFinalPriceTax13(item, quote.businessModeCode),
    );
    const total = amounts.some((amount) => !Number.isFinite(amount))
      ? null
      : amounts.reduce((sum, amount) => sum + Math.round(amount * 100), 0) /
        100;
    return [
      {
        kind: 'purchase-terms',
        values: {
          购买合计: total,
          报价有效期: quote.validUntil || null,
          购买说明: '购买报价以本次导出明细及报价有效期为准',
        },
      },
    ];
  }

  const monthlyRentCents = quoteFinalRentalSubtotalCents(quote);
  const defaults: Record<OutputTermKey, string | number | null> = {
    rentalMethod: quoteWorkbenchBusinessModeDisplayName(
      state.catalog.businessModes,
      quote.businessModeCode,
      quote.businessModeLabelSnapshot,
    ),
    creditReview: '需要',
    depositAmount: Number.isFinite(quote.deposit) ? quote.deposit : null,
    monthlyRent: Number.isFinite(monthlyRentCents)
      ? monthlyRentCents / 100
      : null,
    rentalStartTerm: '3个月',
    complimentaryService: null,
    freight: '租满3个月包一趟运费',
    quoteValidUntil: DEFAULT_OUTPUT_QUOTE_VALIDITY_TEXT,
  };
  const values = Object.fromEntries(
    OUTPUT_TERM_FIELDS.map(([key, label]) => [
      label,
      outputTermDisplayValue(
        key,
        defaults[key],
        quote.outputTermsDisplayOverrides,
      ),
    ]),
  ) as Record<string, string | number | null>;

  return [{ kind: 'rental-terms', values }];
}

export function orderedOutputBusinessEntries<T>(
  kind: string,
  values: Record<string, T>,
): Array<[string, T]> {
  const entries = Object.entries(values) as Array<[string, T]>;
  if (
    kind !== 'rental-terms' ||
    !OUTPUT_TERM_FIELDS.every(([, label]) => Object.hasOwn(values, label))
  )
    return entries;
  const order = new Map<string, number>(
    OUTPUT_TERM_FIELDS.map(([, label], index) => [label, index]),
  );
  return entries.sort(
    ([left], [right]) =>
      (order.get(left) ?? Number.MAX_SAFE_INTEGER) -
      (order.get(right) ?? Number.MAX_SAFE_INTEGER),
  );
}

export function readOutputTermsDisplayOverrides(
  source: Record<string, unknown> | null | undefined,
): OutputTermsDisplayOverrides {
  const restored: OutputTermsDisplayOverrides = {};
  // The legacy record is a complete override set. An explicit {} means
  // “restore defaults”, so it must not resurrect published terms.
  if (source && Object.hasOwn(source, 'outputTermsDisplayOverrides')) {
    const raw = source.outputTermsDisplayOverrides;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return restored;
    for (const [key] of OUTPUT_TERM_FIELDS) {
      const value = (raw as Record<string, unknown>)[key];
      if (typeof value === 'string') restored[key] = value;
    }
    return restored;
  }
  // Published versions normalize the legacy overrides into their authorized,
  // frozen output projection. Never borrow terms from a current catalogue.
  const projection = source?.outputProjection;
  if (
    source?.kind === 'customer-quote-publication-v1' &&
    projection &&
    typeof projection === 'object' &&
    !Array.isArray(projection)
  ) {
    const sections = (projection as Record<string, unknown>).businessSections;
    if (Array.isArray(sections)) {
      for (const section of sections) {
        if (
          !section ||
          typeof section !== 'object' ||
          section.kind !== 'rental-terms'
        )
          continue;
        const values = section.values;
        if (!values || typeof values !== 'object' || Array.isArray(values))
          continue;
        for (const [key, label] of OUTPUT_TERM_FIELDS) {
          if (typeof values[label] === 'string') restored[key] = values[label];
        }
      }
    }
  }
  return restored;
}
